import React, { Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { delay } from "@formant/core/common/delay";
import { logger } from "@formant/core/common/logger";
import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { parseIsoDate } from "@formant/date-fns/parseIsoDate";
import { duration } from "@formant/core/model/duration";
import { IEvent } from "@formant/core/model/IEvent";
import { IEventOrAggregate } from "@formant/core/model/IEventOrAggregate";
import { IFilter } from "@formant/core/model/IFilter";
import { LoadingIndicator } from "@formant/ui/component/common/LoadingIndicator";

import { eventClient, seekClient } from "../../clients";
import { SeekOption, seekOptionToEventTypes } from "../../store/SeekOption";
import { authStore, observeStore } from "../../stores";
import { CircleButton } from "../common/CircleButton";
import { Hotkey } from "../common/hotkey/Hotkey";
import * as styles from "./SeekButton.scss";

export interface ISeekButtonProps {
    filter: IFilter;
    direction: "next" | "previous";
    lighter?: boolean;
    className?: string;
    onSeek?: (time: Date) => void;
    onSelectEvent?: (event: IEventOrAggregate) => void;
    seekOption: SeekOption;
    label?: string;
    hotkeyCode?: string;
}

@observer
export class SeekButton extends Component<ISeekButtonProps> {
    @observable
    private loading: boolean = false;
    @observable
    private error: boolean = false;

    public render() {
        const { loading, error } = this;
        const {
            direction,
            lighter,
            filter,
            className,
            onSeek,
            onSelectEvent,
            seekOption,
            label,
            hotkeyCode,
            ...props
        } = this.props;

        return (
            <CircleButton
                icon={direction === "next" ? "seek-next" : "seek-previous"}
                className={classnames(
                    styles["seek-button"],
                    loading && styles.loading,
                    error && styles.error,
                    lighter && styles.lighter,
                    className,
                )}
                onClicked={this.onSeek}
                data-tooltip={label}
                {...props}
            >
                <LoadingIndicator visible={loading} />
                {label && hotkeyCode && (
                    <Hotkey
                        label={label}
                        code={hotkeyCode}
                        onHotkey={this.onSeek}
                    />
                )}
            </CircleButton>
        );
    }

    private onSeek = async () => {
        const { seekOption, filter, direction, onSeek, onSelectEvent } =
            this.props;

        let to: [Date, IEvent | undefined] | undefined;
        this.loading = true;
        try {
            if (seekOption === "Data presence") {
                try {
                    const { time } = await seekClient.seek(
                        await authStore.token(),
                        {
                            ...filter,
                            direction,
                            from: observeStore.time.toISOString(),
                            start: undefined,
                            end: undefined,
                        },
                    );
                    if (time) {
                        to = [parseIsoDate(time), undefined];
                    }
                } catch (error) {
                    narrowError(
                        error,
                        ResponseError,
                        _ => _.statusCode === 413,
                    );
                    logger.warn("Seek failure caused by large channel");
                }
            } else {
                const eventTypes = seekOptionToEventTypes(seekOption);
                const eventFilter = { ...filter, eventTypes };
                const event = await eventClient.seek(await authStore.token(), {
                    ...eventFilter,
                    direction,
                    from: observeStore.time.toISOString(),
                });
                if (event) {
                    to = [parseIsoDate(event.time), event];
                }
            }
        } finally {
            this.loading = false;
        }

        if (to) {
            const [t, e] = to;
            onSeek?.(t);
            if (e && onSelectEvent) {
                onSelectEvent({ event: e });
            }
        } else {
            this.error = true;
            await delay(1.5 * duration.second);
            this.error = false;
        }
    };
}
